import React, { useEffect, useMemo, useState } from 'react';
import { addGearItem, uploadGearImage } from '../services/gear';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import type { GearCategory, TackleBoxItem, UserProfile, ToastType } from '../types';

interface AddGearModalProps {
  open: boolean;
  onClose: () => void;
  onAdded?: (created?: TackleBoxItem) => void;
  userProfile: UserProfile | null;
  addToast?: (message: string, type?: ToastType) => void;
}

const CATEGORIES: GearCategory[] = ['rod', 'reel', 'lure', 'bait', 'line', 'smart', 'other'];

const AddGearModal: React.FC<AddGearModalProps> = ({
  open,
  onClose,
  onAdded,
  userProfile,
  addToast,
}) => {
  const isDark = resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(isDark) as any;

  const [name, setName] = useState('');
  const [category, setCategory] = useState<GearCategory>('rod');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const [weightOz, setWeightOz] = useState('');
  const [value, setValue] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const safeToast = (msg: string, type: ToastType = 'info') => {
    if (addToast) addToast(msg, type);
    else console.log(`[AddGearModal:${type}] ${msg}`);
  };

  const parsedWeightOz = useMemo(() => {
    const n = Number(weightOz);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [weightOz]);

  const parsedValue = useMemo(() => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }, [value]);

  const resetForm = () => {
    setName('');
    setCategory('rod');
    setBrand('');
    setModel('');
    setNotes('');
    setWeightOz('');
    setValue('');
    setPhotoFile(null);
    setPhotoPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  useEffect(() => {
    if (!open) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  if (!open) return null;

  const handlePickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      safeToast('Please choose an image file.', 'error');
      return;
    }

    const nextUrl = URL.createObjectURL(file);
    setPhotoPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return nextUrl;
    });
    setPhotoFile(file);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      safeToast('Please give your gear a name.', 'error');
      return;
    }

    if (!userProfile?.uid) {
      safeToast('Not logged in.', 'error');
      return;
    }

    setSaving(true);

    try {
      const payload: Omit<TackleBoxItem, 'id'> = {
        name: name.trim(),
        category,
        brand: brand.trim() || undefined,
        model: model.trim() || undefined,
        notes: notes.trim() || undefined,
        weightOz: parsedWeightOz,
        value: parsedValue,
      };

      const id = await addGearItem(userProfile.uid, payload);

      let imageUrl: string | undefined;
      if (photoFile) {
        imageUrl = await uploadGearImage(userProfile.uid, id, photoFile);
      }

      const created: TackleBoxItem = {
        ...payload,
        id,
        imageUrl,
      };

      safeToast('Gear added to your The Hold.', 'success');
      onAdded?.(created);
      resetForm();
      onClose();
    } catch (err) {
      console.error('[AddGearModal] Failed to add gear:', err);
      safeToast('Could not add gear right now. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const shellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDark
      ? '0 16px 34px rgba(3,0,16,0.28), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 14px 30px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.66)',
    color: ui.textPrimary,
  };

  const sectionStyle: React.CSSProperties = {
    background: ui.cardBgMuted ?? ui.surfaceBg ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.10)`,
  };

  const labelStyle: React.CSSProperties = { color: ui.textSecondary };
  const titleStyle: React.CSSProperties = { color: ui.textPrimary };
  const inputStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.inputBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.insetHighlight}, inset 0 1px 0 rgba(255,255,255,0.10)`,
  };
  const subtleButtonStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,0.12)`,
  };
  const accentButtonStyle: React.CSSProperties = {
    background: isDark
      ? 'linear-gradient(180deg,#66D6A9 0%,#42B88B 100%)'
      : 'linear-gradient(180deg,#5B8DFF 0%,#2B6CFF 100%)',
    color: '#FFFFFF',
    boxShadow: isDark
      ? '0 8px 18px rgba(42,150,110,0.22)'
      : '0 8px 18px rgba(43,108,255,0.20)',
  };

  const fieldClass = 'roddo-mobile-form w-full rounded-[13px] px-3.5 py-2.5 text-sm outline-none';

  return (
    <div
      className="fixed inset-x-0 z-[999] roddo-hold-modal overflow-hidden bg-black/40 backdrop-blur-sm"
      style={{
        top: 'calc(var(--roddo-header-h, 0px) + 8px)',
        bottom: 'calc(var(--roddo-nav-h, 76px) + 8px)',
      }}
      onClick={onClose}
    >
      <div
        className="mx-auto h-full min-h-0 w-full max-w-md px-2 py-2"
        style={{
          paddingLeft: 'max(8px, env(safe-area-inset-left))',
          paddingRight: 'max(8px, env(safe-area-inset-right))',
        }}
      >
        <div className="relative h-full overflow-hidden rounded-[22px] roddo-premium-modal-shell" style={shellStyle}>
          <div
            className="relative flex h-full flex-col overflow-hidden rounded-[22px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="shrink-0 border-b px-6 pb-4 pt-5"
              style={{ borderColor: ui.borderStrong ?? ui.border }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 pr-2">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.28em]" style={labelStyle}>
                    The Hold
                  </div>
                  <h2 className="text-[24px] font-extrabold leading-none" style={titleStyle}>
                    Add new gear
                  </h2>
                  <p className="mt-3 text-sm" style={labelStyle}>
                    Save a clean setup entry for your Hold.
                  </p>
                </div>

                <button
                  type="button"
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg transition active:scale-95"
                  style={subtleButtonStyle}
                  onClick={onClose}
                  aria-label="Close add gear modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 [-webkit-overflow-scrolling:touch]">
              <div className="rounded-[18px] p-4" style={sectionStyle}>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                      Gear name
                    </label>
                    <input
                      className={`${fieldClass} roddo-polish-input`}
                      style={inputStyle}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Shimano Curado 200"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                      Category
                    </label>
                    <select
                      className={fieldClass}
                      style={inputStyle}
                      value={category}
                      onChange={(e) => setCategory(e.target.value as GearCategory)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c === 'smart'
                            ? 'Smart gear'
                            : c.charAt(0).toUpperCase() + c.slice(1) + (c === 'bait' ? 's' : '')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                        Brand
                      </label>
                      <input
                        className={fieldClass}
                        style={inputStyle}
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                        Model
                      </label>
                      <input
                        className={fieldClass}
                        style={inputStyle}
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                      Notes
                    </label>
                    <textarea
                      className="roddo-mobile-form w-full rounded-[13px] px-3.5 py-2.5 text-sm outline-none"
                      style={inputStyle}
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Setup, technique, or anything you want to remember."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                        Weight (oz)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={fieldClass}
                        style={inputStyle}
                        value={weightOz}
                        onChange={(e) => setWeightOz(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                        Value (optional)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={fieldClass}
                        style={inputStyle}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em]" style={labelStyle}>
                      Photo (optional)
                    </label>

                    <label
                      className="flex min-h-[88px] cursor-pointer flex-col items-center justify-center rounded-[14px] border border-dashed px-4 py-4 text-center transition hover:brightness-[1.03] active:scale-[.99]"
                      style={{ ...subtleButtonStyle, borderStyle: 'dashed', color: ui.textSecondary }}
                    >
                      <input type="file" accept="image/*" className="hidden" onChange={handlePickPhoto} />
                      <span className="text-[15px] font-semibold" style={{ color: ui.textPrimary }}>
                        {photoPreviewUrl ? 'Change gear photo' : 'Upload gear photo'}
                      </span>
                      <span className="mt-2 text-xs" style={labelStyle}>
                        Add a quick reference shot for this setup.
                      </span>
                    </label>

                    {photoPreviewUrl ? (
                      <div className="mt-3 overflow-hidden rounded-[20px]" style={{ border: `1px solid ${ui.border}` }}>
                        <img src={photoPreviewUrl} alt="Gear preview" className="h-40 w-full object-cover" />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="shrink-0 border-t px-6 py-3"
              style={{
                borderColor: ui.borderStrong ?? ui.border,
                background: ui.cardBgStrong ?? ui.cardBg,
              }}
            >
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="rounded-[12px] px-4 py-2.5 text-sm font-semibold transition active:scale-[.98]"
                  style={subtleButtonStyle}
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="rounded-[12px] px-5 py-2.5 text-sm font-extrabold transition active:scale-[.98] disabled:opacity-60"
                  style={accentButtonStyle}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save gear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGearModal;
